import { Button, DdsDrawCell, BreakdownCell } from "@common"; // BreakdownCell added for hover tooltips on financial columns
import { Link } from "react-router-dom";
import { formatMoney } from "./format";
import {
  LINK_CLASSES,
  DDS_DRAW_COLORS,
  PROFIT_COLORS,
  OTHER_PROFIT_COLORS,
  TOTAL_FEE_COLORS,
  DEDUCTION_COLORS,
  FEE_PERCENT_COLORS,
} from "./columnColors";

// ── Multi-level approval helpers ───────────────────────────────────────
// Backend stores each level as JSON: { approved: bool, user: "<email>" }.
// `null` / `{}` means "not yet reviewed".
export const APPROVAL_STEPS = [
  { key: "am", label: "Area Manager", field: "am_approval", role: "area_manager" },
  { key: "payroll", label: "Payroll", field: "payroll_approval", role: "payroll" },
  { key: "admin", label: "Admin", field: "admin_approval", role: "admin" },
];

export const isApprovalApproved = (slot) =>
  !!slot && typeof slot === "object" && slot.approved === true;
const isApprovalRejected = (slot) => !!slot && typeof slot === "object" && slot.approved === false;
const approvalUser = (slot) => (slot && typeof slot === "object" ? slot.user || null : null);

/**
 * Reset request is pending when `request_reset_approval.request === true`.
 * Admin resolution (approve OR reject) wipes the field back to `{}`.
 */
export const hasPendingResetRequest = (row) => {
  const slot = row?.request_reset_approval;
  if (!slot || typeof slot !== "object") return false;
  return slot.request === true;
};
const resetRequester = (row) => row?.request_reset_approval?.user_request || null;

/**
 * Multi-step approval indicator: Area Manager → Payroll → Admin.
 *
 * Each step renders as a small pill. When the current user has the role
 * that owns a step AND the step is still pending, the pill becomes a
 * clickable button that calls `onApproveStep(row, step)` so the caller
 * can PATCH the corresponding `<level>_approval` JSON field.
 */
const ApprovalStepper = ({
  row,
  onApproveStep,
  canApproveStep,
  onRequestReset,
  canResetStep,
  onResolveReset,
  canResolveReset,
}) => {
  const pendingReset = hasPendingResetRequest(row);
  const requester = resetRequester(row);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        {APPROVAL_STEPS.map((step, idx) => {
          const slot = row[step.field];
          const approved = isApprovalApproved(slot);
          const rejected = isApprovalRejected(slot);
          const userEmail = approvalUser(slot);
          const canApprove = !!canApproveStep?.(step, row);
          const canReset = !!canResetStep?.(step, row);
          const userCanApprove = !approved && !rejected && row.id != null && canApprove;
          // Green pill becomes a reset-trigger when the current user is
          // allowed to reset this step. Admin can reset any step (immediate
          // wipe). AM / Payroll can only reset their own step (pending
          // request). Locked while a reset request is already pending.
          const userCanRequestReset = approved && row.id != null && canReset && !pendingReset;
          const clickable = userCanApprove || userCanRequestReset;

          const baseClass =
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap";
          const stateClass = approved
            ? userCanRequestReset
              ? "bg-palette-green-solid border-palette-green-solid text-white hover:bg-palette-green-badge cursor-pointer"
              : "bg-palette-green-solid border-palette-green-solid text-white"
            : rejected
              ? "bg-palette-red-label border-palette-red-label text-white"
              : userCanApprove
                ? "bg-white border-palette-indigo-ring text-palette-indigo-heading hover:bg-palette-indigo-soft cursor-pointer"
                : "bg-white border-palette-gray-border text-palette-gray-secondary";

          const statusLabel = approved ? "Approved" : rejected ? "Rejected" : "Pending";
          const title = approved
            ? `${step.label}: Approved${userEmail ? ` by ${userEmail}` : ""}${
                userCanRequestReset ? " — click to request reset" : ""
              }`
            : rejected
              ? `${step.label}: Rejected${userEmail ? ` by ${userEmail}` : ""}`
              : userCanApprove
                ? `Click to approve as ${step.label}`
                : `${step.label}: Pending`;
          const icon = approved ? "\u2713" : rejected ? "\u2717" : "\u25CB";
          const content = (
            <>
              <span aria-hidden>{icon}</span>
              <span>{step.label}</span>
            </>
          );

          const tooltip = (
            <span
              className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded bg-palette-gray-primary px-2 py-1 text-[11px] font-normal text-white shadow-lg group-hover:block"
              role="tooltip"
            >
              <span className="font-semibold">{step.label}:</span> {statusLabel}
              {(approved || rejected) && (
                <span className="block text-[10px] text-palette-gray-border">
                  by {userEmail || "unknown"}
                </span>
              )}
              {userCanRequestReset && (
                <span className="block text-[10px] text-palette-gray-border">
                  Click to request reset
                </span>
              )}
              {!approved && !rejected && userCanApprove && (
                <span className="block text-[10px] text-palette-gray-border">Click to approve</span>
              )}
            </span>
          );

          const handleClick = () => {
            if (userCanApprove) onApproveStep?.(row, step);
            else if (userCanRequestReset) onRequestReset?.(row, step);
          };

          return (
            <span key={step.key} className="flex items-center gap-1">
              {clickable ? (
                <span className="group relative inline-block">
                  <button
                    type="button"
                    className={`${baseClass} ${stateClass}`}
                    title={title}
                    onClick={handleClick}
                  >
                    {content}
                  </button>
                  {tooltip}
                </span>
              ) : (
                <span className="group relative inline-block">
                  <span className={`${baseClass} ${stateClass}`} title={title}>
                    {content}
                  </span>
                  {tooltip}
                </span>
              )}
              {idx < APPROVAL_STEPS.length - 1 && (
                <span
                  className={`h-px w-3 ${approved ? "bg-palette-green-indicator" : "bg-palette-gray-border"}`}
                  aria-hidden
                />
              )}
            </span>
          );
        })}
      </div>
      {pendingReset && (
        <div className="flex items-center gap-1 text-[10px]">
          <span
            className="rounded bg-palette-amber-muted px-1.5 py-0.5 font-semibold text-palette-amber-text"
            title={`Reset requested by ${requester || "unknown"}`}
          >
            Reset requested
            {requester ? ` by ${requester}` : ""}
          </span>
          {canResolveReset && (
            <>
              <button
                type="button"
                className="rounded bg-palette-green-badge px-1.5 py-0.5 font-semibold text-white hover:bg-palette-green-hover"
                onClick={() => onResolveReset?.(row, "approve")}
                title="Approve reset — clears all 3 approval levels"
              >
                Approve
              </button>
              <button
                type="button"
                className="rounded bg-palette-gray-secondary px-1.5 py-0.5 font-semibold text-white hover:bg-palette-gray-badge"
                onClick={() => onResolveReset?.(row, "reject")}
                title="Reject reset — approvals stay as-is"
              >
                Reject
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

//--------------------------------Dr Fardeen BROwnsline and RGH---------------------//
// dev id=636, prod id=600
const DR_FARDEEN_BROWNSLINE_REGAL_ROWS = new Set(["636::ON-T-RGH", "600::ON-T-RGH"]);

const isDrFardeenBrownslineRegalTierClinic = (row) =>
  DR_FARDEEN_BROWNSLINE_REGAL_ROWS.has(
    `${Number(row?.practitioner_id)}::${String(row?.clinic || "")
      .trim()
      .toUpperCase()}`,
  );

//--------------------------------Dr Fardeen BROwnsline---------------------//
// dev id=586, prod id=550
const DR_FARDEEN_BROWNSLINE_ROWS = new Set(["586::ON-T-BRO", "550::ON-T-BRO"]);

const isDrFardeenBrownslineTierClinic = (row) =>
  DR_FARDEEN_BROWNSLINE_ROWS.has(
    `${Number(row?.practitioner_id)}::${String(row?.clinic || "")
      .trim()
      .toUpperCase()}`,
  );

//--------------------------------Dr Wael---------------------//
// dev ids: 718 ARD, 491 EXP, 451 FAM | prod ids: 682 ARD, 824 EXP, 415 FAM
const DR_WAEL_TIER_ROWS = new Set([
  "718::ON-S-ARD",
  "682::ON-S-ARD",
  "491::NL-G-EXP",
  "824::NL-G-EXP",
  "451::ON-S-FAM",
  "415::ON-S-FAM",
]);

const isDrWaelTierClinic = (row) =>
  DR_WAEL_TIER_ROWS.has(
    `${Number(row?.practitioner_id)}::${String(row?.clinic || "").toUpperCase()}`,
  );

//------------------------------Dr Tanita------------------//
// dev id=581, prod id=545
const DR_TANITA_TIER_ROWS = new Set(["581::MB-W-SOU", "545::MB-W-SOU"]);

const isDrTanitaTierClinic = (row) =>
  DR_TANITA_TIER_ROWS.has(
    `${Number(row?.practitioner_id)}::${String(row?.clinic || "")
      .trim()
      .toUpperCase()}`,
  );

//------------------------------Dr Tejpaul------------------//
// dev id=617, prod id=581
const DR_TEJPAUL_TIER_ROWS = new Set(["617::ON-A-PAR", "581::ON-A-PAR"]);

const isDrTejpaulTierClinic = (row) =>
  DR_TEJPAUL_TIER_ROWS.has(
    `${Number(row?.practitioner_id)}::${String(row?.clinic || "")
      .trim()
      .toUpperCase()}`,
  );

//------------------------------Dr Leyla------------------//
// dev id=544, prod id=508
const DR_LEYLA_TIER_ROWS = new Set(["544::ON-T-COL", "508::ON-T-COL"]);

const isDrLeylaTierClinic = (row) =>
  DR_LEYLA_TIER_ROWS.has(
    `${Number(row?.practitioner_id)}::${String(row?.clinic || "")
      .trim()
      .toUpperCase()}`,
  );

//------------------------------Dr Kevin------------------//
// dev id=481, prod id=445
const DR_KEVIN_TIER_ROWS = new Set(["481::ON-W-OSW", "445::ON-W-OSW"]);

const isDrKevinTierClinic = (row) =>
  DR_KEVIN_TIER_ROWS.has(
    `${Number(row?.practitioner_id)}::${String(row?.clinic || "")
      .trim()
      .toUpperCase()}`,
  );

//------------------------------Dr Dhavalkumar Patel (2 clinics, sequential ladder)------------------//
// PractitionerProfile.id = 114 on both dev and prod, but each clinic
// has its own Practitioner row with a different practitioner_id:
//   dev:  ARD=541, TIL=529
//   prod: ARD=505, TIL=493
// Sequential combined-ladder across ON-S-ARD → ON-T-TIL.
// See backend `compute_dr_patel_residual_breakdown`.
const DR_PATEL_TIER_ROWS = new Set([
  "541::ON-S-ARD",
  "529::ON-T-TIL",
  "505::ON-S-ARD",
  "493::ON-T-TIL",
]);

const isDrPatelTierClinic = (row) =>
  DR_PATEL_TIER_ROWS.has(
    `${Number(row?.practitioner_id)}::${String(row?.clinic || "")
      .trim()
      .toUpperCase()}`,
  );

//-----------------------------------------------------------------------------------------------------------------------------------------//
export const isGridTierDoctor = (row) =>
  isDrWaelTierClinic(row) ||
  isDrTanitaTierClinic(row) ||
  isDrTejpaulTierClinic(row) ||
  isDrLeylaTierClinic(row) ||
  isDrKevinTierClinic(row) ||
  isDrFardeenBrownslineTierClinic(row) ||
  isDrFardeenBrownslineRegalTierClinic(row) ||
  isDrPatelTierClinic(row);

/**
 * DDS Draw is calculated by the backend. This module only consumes the
 * `breakdown` dict the API attaches to every row. See
 * `apps/monthly_compensation/calculations.py` for the canonical formula.
 */
export const calculateDdsDraw = (row) => toNum(row?.breakdown?.dds_draw);

export const getDdsHistoryKey = (row) =>
  `${row?.clinic ?? ""}::${row?.practitioner_id ?? row?.practitioner ?? ""}`;

export const getDdsTrendDirection = (currentDraw, previousDraw) => {
  if (previousDraw === null || previousDraw === undefined) return null;
  if (currentDraw > previousDraw) return "up";
  if (currentDraw < previousDraw) return "down";
  return "flat";
};

const percent = (n) => {
  if (n === null || n === undefined || n === "") return "-";
  const v = Number(n);
  if (Number.isNaN(v)) return "-";
  return `${v.toFixed(2)}%`;
};

const textOrDash = (v) => (v != null && String(v).trim() !== "" ? v : "-");

export const hasAssociateFee = (r) => {
  const v = r.associate_fee_percentage;
  if (v === null || v === undefined || v === "") return false;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0;
};

// Tier-grid doctors are paid via a base-pay ladder rather than an
// associate-fee %. Treat them as "fee configured" so PDF / email / etc.
// stay unlocked even though `associate_fee_percentage` is blank.
// Per-day doctors are paid as `per_day_number * per_day_value` and also
// don't carry an associate fee %. Treat them the same way.
export const isPerDayDoctor = (row) => row?.breakdown?.basis_label === "Per Day";

export const hasFeeConfigured = (r) =>
  hasAssociateFee(r) || isGridTierDoctor(r) || isPerDayDoctor(r);

// ── Deductions (purple columns) ─────────────────────────────────────
// These are the per-row deduction fields that are summed into the
// "Total Fee" column. The column itself acts as an expand/collapse
// trigger for these underlying columns.
export const DEDUCTION_COLUMN_KEYS = [
  "lab_cost",
  "material_cost",
  "invisalign_cost",
  "anesthesia_fee",
  "anesthesiologist_fee",
  "facility_fee",
  "botox",
  "production_adjustment",
  "collection_adjustment",
  "other_deduction",
  "insurance",
];

const SIMPLE_DEDUCTION_FIELDS = [
  "lab_cost",
  "material_cost",
  "invisalign_cost",
  "anesthesia_fee",
  "anesthesiologist_fee",
  "facility_fee",
  "botox",
  "production_adjustment",
  "collection_adjustment",
  "insurance",
];

// One deduction row's dollar amount. A fixed-amount row carries it directly;
// an hourly row is rate x hours; the oldest rows only ever had `profit`. The
// backend resolves `amount` on save, so this only has to derive it for rows
// read straight off an unsaved form or an old record.
const otherDeductionAmount = (t) => {
  const explicit = Number(t?.amount);
  if (Number.isFinite(explicit) && explicit !== 0) return explicit;
  if (t?.mode === "amount") return 0;
  if (t?.hour_rate !== undefined || t?.hours !== undefined) {
    return (Number(t?.hour_rate) || 0) * (Number(t?.hours) || 0);
  }
  return Number(t?.profit) || 0;
};

/**
 * Total of all purple "deduction" columns for a row. Mirrors what the
 * DDS-Draw tooltip subtracts from the basis, end-to-end:
 *
 *   basis − Σ deductions[i].value  (where deducted !== false)
 *          − other_deduction_total
 *          − insurance              ← shown below the divider in the tooltip
 *
 * The backend already aggregates the first two into `breakdown.total_deductions`
 * (see `calculations.py`: `total_deductions += od_total`), and exposes
 * `breakdown.insurance` separately. We just add them.
 *
 * Fallback: if the backend hasn't shipped the breakdown yet, sum the raw
 * row fields the same way the per-column renderers do.
 */
export const computeTotalDeductions = (row) => {
  const b = row?.breakdown;
  if (b && (b.total_deductions != null || b.insurance != null)) {
    return toNum(b.total_deductions) + toNum(b.insurance);
  }
  // Fallback: sum the raw row fields. Mirrors the per-column renderers.
  let total = 0;
  for (const k of SIMPLE_DEDUCTION_FIELDS) total += toNum(row?.[k]);
  const tiers = (Array.isArray(row?.other_deduction) ? row.other_deduction : []) || [];
  for (const t of tiers) total += Number(otherDeductionAmount(t)) || 0;
  return total;
};

/**
 * Header cell for the Total Fee column: shows the label plus a tiny
 * +/- toggle button that expands or collapses all the underlying
 * deduction columns.
 */
export const TotalFeeHeader = ({ expanded, onToggle }) => (
  <div className="flex items-center justify-center gap-1">
    <span>TOTAL FEE</span>
    {onToggle && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="inline-flex h-4 w-4 items-center justify-center rounded border border-palette-slate-placeholder bg-white text-[10px] font-bold leading-none text-palette-slate-heading hover:bg-palette-slate-muted"
        title={expanded ? "Collapse deductions" : "Expand deductions"}
        aria-label={expanded ? "Collapse deductions" : "Expand deductions"}
      >
        {expanded ? "\u2212" : "+"}
      </button>
    )}
  </div>
);

// Inline spinner sized to replace a 20px action icon in place, so the row does
// not reflow while a PDF is being built. Same markup as AdpNetsuite's.
const ActionSpinner = () => (
  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

export const payrollColumns = (
  onUpdateClick,
  {
    onOpenPdf,
    onDownloadLabInvoices,
    onPreviewPdf,
    onSendEmail,
    onApproveStep,
    canApproveStep,
    onRequestReset,
    canResetStep,
    onResolveReset,
    canResolveReset,
    onCompareHistory,
    onPractitionerClick,
    previousMonthDraws,
    allRows = [],
    deductionsExpanded = false,
    onToggleDeductions,
    // { id, action } for the row action currently running, or null. Scoped to a
    // row+action rather than a bare boolean because the mutations are shared:
    // a global `isPending` would spin every row's button at once.
    pdfBusy = null,
  } = {},
) => [
  {
    key: "clinic",
    header: "CLINIC",
    width: 100,
    render: (r) => {
      const code = r.clinic;
      if (!code) return textOrDash(code);
      const monthParam = /^\d{4}-\d{2}/.test(String(r.month || ""))
        ? String(r.month).slice(0, 7)
        : "";
      const to = `/report/${encodeURIComponent(code)}?tab=monthly${
        monthParam ? `&month=${monthParam}` : ""
      }`;
      return (
        <Link to={to} title={`Open ${code} monthly reconciliation`} className={LINK_CLASSES}>
          {code}
        </Link>
      );
    },
  },
  {
    key: "practitioner_name",
    header: "PRACTITIONER",
    width: 160,
    render: (r) => {
      const name = textOrDash(r.practitioner_name);
      const showGridBadge = isGridTierDoctor(r);
      const showPerDayBadge = r?.breakdown?.basis_label === "Per Day";

      const nameContent = (
        <div className="flex flex-col items-start gap-1">
          <span>{name}</span>

          {showGridBadge && (
            <span
              title="Grid calculation"
              className="inline-flex items-center rounded-full bg-palette-emerald-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-palette-emerald-hover"
            >
              Grid
            </span>
          )}

          {showPerDayBadge && (
            <span
              title="Per Day calculation"
              className="inline-flex items-center rounded-full bg-palette-amber-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-palette-amber-heading"
            >
              Per Day
            </span>
          )}
        </div>
      );

      if (!onPractitionerClick || name === "-" || !r.practitioner_id) {
        return nameContent;
      }

      return (
        <button
          type="button"
          onClick={() => onPractitionerClick(r)}
          title="View practitioner profile"
          className="text-left font-semibold text-palette-indigo-heading hover:text-palette-indigo-primary focus:outline-none focus:ring-2 focus:ring-palette-indigo-hover rounded"
        >
          {nameContent}
        </button>
      );
    },
  },
  {
    key: "dds_draw",
    header: "DDS DRAW",
    width: 190,
    headerClassName: DDS_DRAW_COLORS.header,
    cellClassName: DDS_DRAW_COLORS.cell,
    render: (r) => {
      // DDS Draw is computed by the backend for everyone (standard formula
      // and per-doctor tier ladders alike). The frontend just renders
      // `row.breakdown.dds_draw`.
      if (!hasFeeConfigured(r)) {
        return <span className="text-palette-gray-placeholder text-xs italic">Set fee %</span>;
      }
      const draw = toNum(r?.breakdown?.dds_draw);
      const previousDraw = previousMonthDraws?.[getDdsHistoryKey(r)];
      const trendDirection = getDdsTrendDirection(draw, previousDraw);
      return (
        <DdsDrawCell row={r} trendDirection={trendDirection} onCompareHistory={onCompareHistory} />
      );
    },
  },
  {
    key: "production",
    header: "PRODUCTION",
    width: 100,
    headerClassName: PROFIT_COLORS.header,
    cellClassName: PROFIT_COLORS.cell,
    render: (r) =>
      toNum(r.production) === 0 ? (
        <span className="text-palette-sky-heading/70 text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.production}
          breakdown={r.production_breakdown}
          label="Production"
        />
      ),
  },
  {
    key: "collection",
    header: "COLLECTION",
    width: 100,
    headerClassName: PROFIT_COLORS.header,
    cellClassName: PROFIT_COLORS.cell,
    render: (r) =>
      toNum(r.collection) === 0 ? (
        <span className="text-palette-sky-heading/70 text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.collection}
          breakdown={r.collection_breakdown}
          label="Collection"
        />
      ),
  },
  {
    key: "other_profit",
    header: "OTHER PROFIT",
    width: 110,
    headerClassName: OTHER_PROFIT_COLORS.header,
    cellClassName: OTHER_PROFIT_COLORS.cell,
    render: (r) => {
      // Prefer the authoritative breakdown.other_profit (only includes
      // entries with profit > 0); fall back to the raw saved tiers.
      // Grid-tier doctors get a real `other_profit` here too — their ladder
      // pay is a separate thing, exposed via `breakdown.tier_breakdown`, so
      // this column is only ever the extra earned on top.
      const tiers =
        (Array.isArray(r?.breakdown?.other_profit) && r.breakdown.other_profit.length > 0
          ? r.breakdown.other_profit
          : Array.isArray(r.other_profit)
            ? r.other_profit
            : []) || [];
      if (tiers.length === 0)
        return <span className="text-palette-gray-placeholder text-xs">N/A</span>;
      const total =
        toNum(r?.breakdown?.other_profit_total) ||
        tiers.reduce((sum, t) => sum + toNum(t?.profit), 0);
      const title = tiers
        .map((t) => {
          const pct = Number(t?.percent) || 0;
          const input = toNum(t?.input_value);
          const profit = toNum(t?.profit);
          return `${t?.label || "?"} (${pct}%): ${formatMoney(input)} → ${formatMoney(profit)}`;
        })
        .join("\n");
      return (
        <span className="text-palette-sky-primary cursor-help" title={title}>
          {formatMoney(total)}
        </span>
      );
    },
  },
  {
    key: "total_fee",
    header: <TotalFeeHeader expanded={deductionsExpanded} onToggle={onToggleDeductions} />,
    width: 110,
    align: "center",
    headerClassName: TOTAL_FEE_COLORS.header,
    cellClassName: TOTAL_FEE_COLORS.cell,
    render: (r) => {
      const total = computeTotalDeductions(r);
      if (total === 0) return <span className="text-palette-gray-placeholder text-xs">N/A</span>;
      return <span className="font-semibold text-palette-rose-primary">{formatMoney(total)}</span>;
    },
  },
  {
    key: "lab_cost",
    header: "LAB COSTS",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.lab_cost) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.lab_cost}
          breakdown={r.lab_cost_breakdown}
          label="Lab Costs"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "material_cost",
    header: "MATERIAL",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.material_cost) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.material_cost}
          breakdown={r.material_cost_breakdown}
          label="Material"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "invisalign_cost",
    header: "INVISALIGN",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.invisalign_cost) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.invisalign_cost}
          breakdown={r.invisalign_cost_breakdown}
          label="Invisalign"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "anesthesia_fee",
    header: "ANESTHESIA",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.anesthesia_fee) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.anesthesia_fee}
          breakdown={r.anesthesia_fee_breakdown}
          label="Anesthesia"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "anesthesiologist_fee",
    header: "ANESTHESIOLOGIST",
    width: 110,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.anesthesiologist_fee) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.anesthesiologist_fee}
          breakdown={r.anesthesiologist_fee_breakdown}
          label="Anesthesiologist"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "facility_fee",
    header: "FACILITY",
    width: 80,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.facility_fee) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.facility_fee}
          breakdown={r.facility_fee_breakdown}
          label="Facility"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "botox",
    header: "BOTOX",
    width: 70,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.botox) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.botox}
          breakdown={r.botox_breakdown}
          label="Botox"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "production_adjustment",
    header: "PRODUCTION ADJ.",
    width: 70,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.production_adjustment) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.production_adjustment}
          breakdown={r.production_adjustment_breakdown}
          label="Production Adj."
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "collection_adjustment",
    header: "COLLECTION ADJ.",
    width: 70,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.collection_adjustment) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.collection_adjustment}
          breakdown={r.collection_adjustment_breakdown}
          label="Collection Adj."
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "other_deduction",
    header: "OTHER DEDUCTION",
    width: 110,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) => {
      const tiers =
        (Array.isArray(r?.breakdown?.other_deduction) && r.breakdown.other_deduction.length > 0
          ? r.breakdown.other_deduction
          : Array.isArray(r.other_deduction)
            ? r.other_deduction
            : []) || [];
      if (tiers.length === 0)
        return <span className="text-palette-gray-placeholder text-xs">N/A</span>;
      const amountOf = (t) => {
        const explicit = Number(t?.amount);
        if (Number.isFinite(explicit) && explicit !== 0) return explicit;
        if (t?.hour_rate !== undefined || t?.hours !== undefined) {
          return (Number(t?.hour_rate) || 0) * (Number(t?.hours) || 0);
        }
        return Number(t?.profit) || 0;
      };
      const total =
        toNum(r?.breakdown?.other_deduction_total) ||
        tiers.reduce((sum, t) => sum + (Number(amountOf(t)) || 0), 0);
      const title = tiers
        .map((t) => {
          const amt = amountOf(t);
          if (t?.hour_rate !== undefined || t?.hours !== undefined) {
            const hr = Number(t?.hour_rate) || 0;
            const hrs = Number(t?.hours) || 0;
            return `${t?.label || "?"} (${hrs}h × ${formatMoney(hr)}): ${formatMoney(amt)}`;
          }
          const pct = Number(t?.percent) || 0;
          const input = toNum(t?.input_value);
          return `${t?.label || "?"} (${pct}%): ${formatMoney(input)} → ${formatMoney(amt)}`;
        })
        .join("\n");
      return (
        <span className="text-palette-slate-primary cursor-help" title={title}>
          {formatMoney(total)}
        </span>
      );
    },
  },
  {
    key: "insurance",
    header: "INSURANCE",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.insurance) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <span className="text-palette-slate-primary">{formatMoney(r.insurance)}</span>
      ),
  },
  {
    key: "hours_worked",
    header: "HOURS",
    width: 70,
    render: (r) =>
      toNum(r.hours_worked) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          showBreakdown={r.clinic === "ON-W-ERD"}
          value={r.hours_worked}
          breakdown={r.hours_worked_breakdown}
          label="Hours"
          formatter={(v) => Number(v).toFixed(2)}
        />
      ),
  },
  {
    key: "associate_fee_percentage",
    header: "ASSOCIATE FEE %",
    width: 80,
    headerClassName: FEE_PERCENT_COLORS.header,
    cellClassName: FEE_PERCENT_COLORS.cell,
    render: (r) => {
      if (!hasAssociateFee(r) && !isGridTierDoctor(r) && onUpdateClick) {
        return (
          <Button variant="gray" size="xs" onClick={() => onUpdateClick(r)}>
            Update
          </Button>
        );
      }
      if (isGridTierDoctor(r) && !hasAssociateFee(r)) {
        return <span className="text-xs text-palette-emerald-hover font-semibold">GRID</span>;
      }
      return percent(r.associate_fee_percentage);
    },
  },
  {
    key: "approval",
    header: "APPROVAL",
    width: 280,
    align: "center",
    headerClassName: "text-center",
    render: (r) => (
      <div className="flex justify-center">
        <ApprovalStepper
          row={r}
          onApproveStep={onApproveStep}
          canApproveStep={canApproveStep}
          onRequestReset={onRequestReset}
          canResetStep={canResetStep}
          onResolveReset={onResolveReset}
          canResolveReset={canResolveReset}
        />
      </div>
    ),
  },
  ...(onOpenPdf || onDownloadLabInvoices || onPreviewPdf
    ? [
        {
          key: "pdf",
          header: "PDF",
          width: 100,
          render: (r) => {
            const disabled = !hasFeeConfigured(r);
            const labInvoicesDisabled = !r.clinic || !r.practitioner_id || !r.month;
            // The statement PDF now makes a NetSuite call to resolve each
            // month's check, so these are seconds-long actions. Any running
            // action locks the whole row's group: firing a second one would
            // rebuild the same PDF and leave the spinner on the wrong button.
            const busyAction = pdfBusy?.id === r.id ? pdfBusy.action : null;
            const rowBusy = !!busyAction;
            return (
              <div className="flex items-center justify-center gap-1">
                {onOpenPdf && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => !disabled && !rowBusy && onOpenPdf(r)}
                    disabled={disabled || rowBusy}
                    title={
                      disabled
                        ? "Please update Associate Fee % first"
                        : busyAction === "download"
                          ? "Building statement PDF…"
                          : rowBusy
                            ? "Please wait…"
                            : "Download statement PDF"
                    }
                    className={`!p-1 !rounded-lg ${disabled ? "!text-palette-gray-border" : "!text-palette-indigo-badge hover:!bg-palette-indigo-soft hover:!text-palette-indigo-heading"}`}
                  >
                    {busyAction === "download" ? (
                      <ActionSpinner />
                    ) : (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3"
                        />
                      </svg>
                    )}
                  </Button>
                )}
                {onPreviewPdf && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => !disabled && !rowBusy && onPreviewPdf(r)}
                    disabled={disabled || rowBusy}
                    title={
                      disabled
                        ? "Please update Associate Fee % first"
                        : busyAction === "preview"
                          ? "Building preview…"
                          : rowBusy
                            ? "Please wait…"
                            : "Preview PDF"
                    }
                    className={`!p-1 !rounded-lg ${disabled ? "!text-palette-gray-border" : "!text-palette-purple-badge hover:!bg-palette-purple-soft hover:!text-palette-purple-heading"}`}
                  >
                    {busyAction === "preview" ? (
                      <ActionSpinner />
                    ) : (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </Button>
                )}
                {onDownloadLabInvoices && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => !labInvoicesDisabled && !rowBusy && onDownloadLabInvoices(r)}
                    disabled={labInvoicesDisabled || rowBusy}
                    title={
                      labInvoicesDisabled
                        ? "Clinic, practitioner, and month are required"
                        : busyAction === "labInvoices"
                          ? "Collecting lab invoices…"
                          : rowBusy
                            ? "Please wait…"
                            : "Download monthly lab invoice PDFs"
                    }
                    className={`!p-1 !rounded-lg ${
                      labInvoicesDisabled
                        ? "!text-palette-gray-border"
                        : "!text-palette-emerald-badge hover:!bg-palette-emerald-soft hover:!text-palette-emerald-hover"
                    }`}
                  >
                    {busyAction === "labInvoices" ? (
                      <ActionSpinner />
                    ) : (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 7h5l2 2h9v9a2 2 0 01-2 2H6a2 2 0 01-2-2V7zm8 5v5m0 0l-2-2m2 2l2-2"
                        />
                      </svg>
                    )}
                  </Button>
                )}
              </div>
            );
          },
        },
      ]
    : []),
  ...(onSendEmail
    ? [
        {
          key: "email",
          header: "EMAIL",
          width: 45,
          render: (r) => {
            const noEmail = !r.practitioner_email;
            // Email is gated by ADMIN approval (final step of the
            // Area Manager → Payroll → Admin chain).
            const adminApproved = isApprovalApproved(r.admin_approval);
            const noApproval = !adminApproved;
            const alreadySent = !!r.email_sent_at;

            if (noEmail && onUpdateClick) {
              return (
                <Button
                  variant="gray"
                  size="xs"
                  onClick={() => onUpdateClick(r)}
                  title="No email address - click to update practitioner profile"
                >
                  Update
                </Button>
              );
            }

            const disabled = noApproval;
            const title = noApproval
              ? "Admin approval required before sending email"
              : alreadySent
                ? `Last sent ${new Date(r.email_sent_at).toLocaleString()} — click to resend to ${r.practitioner_email}`
                : `Send PDF to ${r.practitioner_email}`;
            const colorClasses = disabled
              ? "!text-palette-gray-border"
              : alreadySent
                ? "!text-palette-green-badge hover:!bg-palette-green-soft hover:!text-palette-green-hover"
                : "!text-palette-indigo-badge hover:!bg-palette-indigo-soft hover:!text-palette-indigo-heading";
            return (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => !disabled && onSendEmail(r)}
                disabled={disabled}
                title={title}
                className={`!p-1 !rounded-lg ${colorClasses}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </Button>
            );
          },
        },
      ]
    : []),
  {
    key: "email_sent_at",
    header: "EMAILED",
    width: 100,
    render: (r) => {
      if (!r.email_sent_at) return <span className="text-palette-gray-placeholder text-xs">-</span>;
      const d = new Date(r.email_sent_at);
      return (
        <span className="text-palette-green-hover text-xs font-medium" title={d.toLocaleString()}>
          {"\u2713"} {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </span>
      );
    },
  },
];

export function toSortableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const cleaned = String(value).replace(/[$,%\s,]/g, "");
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

export function getComparableValue(row, key) {
  switch (key) {
    case "dds_draw":
      return calculateDdsDraw(row);
    case "approval":
      return isApprovalApproved(row.admin_approval) ? 1 : 0;
    case "email_sent_at":
      return row.email_sent_at ? new Date(row.email_sent_at).getTime() : null;
    case "pdf":
    case "email":
    case "_select":
      return null;
    default:
      return row?.[key];
  }
}

export function getFilterLabel(row, key) {
  const value = getComparableValue(row, key);

  if (key === "approval") return value ? "Approved" : "Pending";
  if (key === "email_sent_at") return value ? "Emailed" : "Not emailed";
  if (value === null || value === undefined || value === "") return "(Blank)";

  return String(value);
}

export function compareValues(a, b, key) {
  if (a === b) return 0;
  if (a === null || a === undefined || a === "") return 1;
  if (b === null || b === undefined || b === "") return -1;

  if (NUMERIC_COLUMN_KEYS.has(key)) {
    const aNum = toSortableNumber(a);
    const bNum = toSortableNumber(b);
    if (aNum === bNum) return 0;
    if (aNum === null) return 1;
    if (bNum === null) return -1;
    return aNum - bNum;
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export function formatHeaderTotal(key, total) {
  const config = TOTALABLE_COLUMN_CONFIG[key];
  if (!config) return null;
  if (config.type === "text") return config.label ?? key;
  if (config.type === "money") return formatMoney(total ?? 0);
  const decimals = config.decimals ?? 0;
  return Number(total ?? 0).toFixed(decimals);
}

export const MAX_FILTER_OPTIONS = 200;

export const NUMERIC_COLUMN_KEYS = new Set([
  "dds_draw",
  "production",
  "collection",
  "lab_cost",
  "material_cost",
  "invisalign_cost",
  "anesthesia_fee",
  "anesthesiologist_fee",
  "facility_fee",
  "botox",
  "adjustment",
  "hours_worked",
  "associate_fee_percentage",
  "approval",
]);

export const DESC_ASC_LABEL_KEYS = new Set([...NUMERIC_COLUMN_KEYS, "email_sent_at"]);

export const TOTALABLE_COLUMN_CONFIG = {
  practitioner_name: { type: "text", label: "COLUMN TOTAL" },
  dds_draw: { type: "money" },
  production: { type: "money" },
  collection: { type: "money" },
  lab_cost: { type: "money" },
  material_cost: { type: "money" },
  invisalign_cost: { type: "money" },
  anesthesia_fee: { type: "money" },
  anesthesiologist_fee: { type: "money" },
  facility_fee: { type: "money" },
  botox: { type: "money" },
  adjustment: { type: "money" },
  hours_worked: { type: "number", decimals: 2 },
};

export const TOTAL_BADGE_COLORS = {
  practitioner_name: "bg-palette-slate-secondary",
  dds_draw: "bg-palette-emerald-badge",
  production: "bg-palette-sky-badge",
  collection: "bg-palette-sky-badge",
  lab_cost: "bg-palette-slate-secondary",
  material_cost: "bg-palette-slate-secondary",
  invisalign_cost: "bg-palette-slate-secondary",
  anesthesia_fee: "bg-palette-slate-secondary",
  anesthesiologist_fee: "bg-palette-slate-secondary",
  facility_fee: "bg-palette-slate-secondary",
  botox: "bg-palette-slate-secondary",
  adjustment: "bg-palette-amber-badge",
  hours_worked: "bg-palette-sky-badge",
};

// ─────────────────────────────────────────────────────────────────────
// Inline-edit field configuration (Associate Fee table)
//
// Single source of truth for which monthly-compensation columns are
// inline-editable, which require a value when creating a new row, and
// the human-readable labels used in validation toasts.
// ─────────────────────────────────────────────────────────────────────

export const INLINE_EDITABLE_MONTHLY_FIELDS = [
  "production",
  "collection",
  "lab_cost",
  "material_cost",
  "invisalign_cost",
  "anesthesia_fee",
  "anesthesiologist_fee",
  "facility_fee",
  "botox",
  "production_adjustment",
  "collection_adjustment",
  "hours_worked",
];

// Practitioner-level fields editable from the Associate Fee table.
// NOTE: `associate_fee_percentage` is intentionally NOT inline-editable —
// it must be changed via the practitioner edit modal.
export const INLINE_EDITABLE_PRACTITIONER_FIELDS = ["insurance"];

export const INLINE_EDITABLE_FIELDS = [
  ...INLINE_EDITABLE_PRACTITIONER_FIELDS,
  ...INLINE_EDITABLE_MONTHLY_FIELDS,
];

export const INLINE_REQUIRED_CREATE_FIELDS = new Set(["production", "collection"]);

export const INLINE_ACTION_COLUMN_KEY = "_inline_actions";

export const INLINE_FIELD_LABELS = {
  associate_fee_percentage: "Associate fee %",
  insurance: "Insurance",
  production: "Production",
  collection: "Collection",
  lab_cost: "Lab costs",
  material_cost: "Material costs",
  invisalign_cost: "Invisalign",
  anesthesia_fee: "Anesthesia",
  anesthesiologist_fee: "Anesthesiologist",
  facility_fee: "Facility",
  botox: "Botox",
  production_adjustment: "Production adjustment",
  collection_adjustment: "Collection adjustment",
  hours_worked: "Hours worked",
};

// ── Excel export ────────────────────────────────────────────────────
// Flattens the table's rows into plain, Excel-friendly records: numbers
// stay numbers (so Excel can sum them) and the JSX-only columns
// (approval stepper, PDF/email buttons) become readable text.

const tierTotal = (row, key, totalKey, amountOf) => {
  const tiers =
    (Array.isArray(row?.breakdown?.[key]) && row.breakdown[key].length > 0
      ? row.breakdown[key]
      : Array.isArray(row?.[key])
        ? row[key]
        : []) || [];
  if (tiers.length === 0) return 0;
  return toNum(row?.breakdown?.[totalKey]) || tiers.reduce((sum, t) => sum + amountOf(t), 0);
};

const approvalLabel = (slot) => {
  if (isApprovalApproved(slot)) return "Approved";
  if (isApprovalRejected(slot)) return "Rejected";
  return "Pending";
};

export const buildAssociateFeeExportRows = (rows = []) =>
  rows.map((r) => ({
    MONTH: r.month ? String(r.month).slice(0, 7) : "",
    CLINIC: r.clinic || "",
    PRACTITIONER: r.practitioner_name || "",
    "DDS DRAW": calculateDdsDraw(r),
    PRODUCTION: toNum(r.production),
    COLLECTION: toNum(r.collection),
    "OTHER PROFIT": tierTotal(r, "other_profit", "other_profit_total", (t) => toNum(t?.profit)),
    "TOTAL FEE": computeTotalDeductions(r),
    "LAB COSTS": toNum(r.lab_cost),
    MATERIAL: toNum(r.material_cost),
    INVISALIGN: toNum(r.invisalign_cost),
    ANESTHESIA: toNum(r.anesthesia_fee),
    ANESTHESIOLOGIST: toNum(r.anesthesiologist_fee),
    FACILITY: toNum(r.facility_fee),
    BOTOX: toNum(r.botox),
    "PRODUCTION ADJ": toNum(r.production_adjustment),
    "COLLECTION ADJ": toNum(r.collection_adjustment),
    "OTHER DEDUCTION": tierTotal(
      r,
      "other_deduction",
      "other_deduction_total",
      otherDeductionAmount,
    ),
    INSURANCE: toNum(r.insurance),
    HOURS: toNum(r.hours_worked),
    "ASSOCIATE FEE %":
      r.associate_fee_percentage === null ||
      r.associate_fee_percentage === undefined ||
      r.associate_fee_percentage === ""
        ? isGridTierDoctor(r)
          ? "GRID"
          : ""
        : toNum(r.associate_fee_percentage),
    "AM APPROVAL": approvalLabel(r.am_approval),
    "PAYROLL APPROVAL": approvalLabel(r.payroll_approval),
    "ADMIN APPROVAL": approvalLabel(r.admin_approval),
    EMAILED: r.email_sent_at ? new Date(r.email_sent_at).toLocaleString() : "",
  }));
